import React, { useState, useEffect } from 'react';
import { AlertTriangle, Wifi, WifiOff } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export const OfflineIndicator: React.FC = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showOfflineAlert, setShowOfflineAlert] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowOfflineAlert(false);
      toast({
        title: "✅ Connexion rétablie",
        description: "Vous êtes de nouveau en ligne",
      });
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowOfflineAlert(true);
      toast({
        title: "⚠️ Mode hors ligne",
        description: "Vous travaillez hors ligne. Les données seront synchronisées à la reconnexion.",
        variant: "destructive",
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [toast]);

  if (isOnline) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50">
      <div className="bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2">
        <WifiOff className="h-4 w-4" />
        <span className="text-sm font-medium">Mode hors ligne</span>
      </div>
    </div>
  );
};

// Hook pour vérifier l'état de connexion
export const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
};

// Service pour gérer les données hors ligne
export class OfflineDataManager {
  private static instance: OfflineDataManager;
  private pendingActions: Array<{
    id: string;
    action: 'create' | 'update' | 'delete';
    data: any;
    timestamp: number;
  }> = [];

  static getInstance(): OfflineDataManager {
    if (!OfflineDataManager.instance) {
      OfflineDataManager.instance = new OfflineDataManager();
    }
    return OfflineDataManager.instance;
  }

  // Sauvegarder une action en attente
  addPendingAction(action: 'create' | 'update' | 'delete', data: any) {
    const pendingAction = {
      id: `action_${Date.now()}_${Math.random()}`,
      action,
      data,
      timestamp: Date.now(),
    };

    this.pendingActions.push(pendingAction);
    this.saveToLocalStorage();
  }

  // Synchroniser les actions en attente
  async syncPendingActions() {
    if (this.pendingActions.length === 0) return;

    console.log(`Synchronisation de ${this.pendingActions.length} actions en attente...`);

    for (const action of this.pendingActions) {
      try {
        // Ici, vous pouvez implémenter la logique de synchronisation
        // avec votre backend Supabase
        console.log('Synchronisation:', action);
        
        // Simuler une synchronisation réussie
        await new Promise(resolve => setTimeout(resolve, 100));
        
      } catch (error) {
        console.error('Erreur de synchronisation:', error);
        // Garder l'action pour une nouvelle tentative
        continue;
      }
    }

    // Vider les actions synchronisées
    this.pendingActions = [];
    this.saveToLocalStorage();
  }

  // Sauvegarder dans localStorage
  private saveToLocalStorage() {
    try {
      localStorage.setItem('offline_pending_actions', JSON.stringify(this.pendingActions));
    } catch (error) {
      console.error('Erreur sauvegarde localStorage:', error);
    }
  }

  // Charger depuis localStorage
  loadFromLocalStorage() {
    try {
      const saved = localStorage.getItem('offline_pending_actions');
      if (saved) {
        this.pendingActions = JSON.parse(saved);
      }
    } catch (error) {
      console.error('Erreur chargement localStorage:', error);
    }
  }

  // Obtenir le nombre d'actions en attente
  getPendingActionsCount(): number {
    return this.pendingActions.length;
  }

  // Nettoyer les anciennes actions (plus de 24h)
  cleanupOldActions() {
    const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
    this.pendingActions = this.pendingActions.filter(
      action => action.timestamp > oneDayAgo
    );
    this.saveToLocalStorage();
  }
}

export default OfflineIndicator; 